import * as React from 'react';
import {
    makeStyles,
    shorthands,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogTrigger,
    Button,
    DialogSurface,
    DialogActions,
    DialogBody,
    RadioGroup,
    Radio,
    Checkbox
} from '@fluentui/react-components';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastProvider';
import { refreshAllCitations } from '../utils/citationRefresh';
import { getStoredCitationMapping } from '../utils/documentProperties';

const useStyles = makeStyles({
    container: {
        height: '100%',
        height: '100%',
        backgroundColor: '#eee',
        padding: '20px',
        borderRadius: '0px',
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
    },
    radioGroup: {
        flex: 1,
        display: 'flex',
    },
    btn: {
        '&:disabled': {
            color: '#BDBDBD',
            backgroundColor: '#F0F0F0',
        }
    },
    buttons: {
        display: 'flex',
        flexDirection: 'row!important',
        justifyContent: 'flex-start',
        marginTop: '20px',
        ...shorthands.gap('10px'),
    },
    actionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
    },
    signOutContainer: {
        width: '100%',
        padding: '0 20px 20px 20px',
        marginTop: '10px',
    },
    btnClose: {
        padding: '0px',
        justifyContent: 'flex-end',
        float: 'right',
    },
    divider: {
        width: '100%',
        height: '1px',
        backgroundColor: '#ccc',
        margin: '10px 0',
    },
    signOutButton: {
        width: '100%',
        color: '#666',
        fontSize: '14px',
        padding: '10px 16px',
        backgroundColor: '#f5f5f5',
        border: '1px solid #ddd',
        marginTop: '10px',
        marginBottom: '10px',
        '&:hover': {
            backgroundColor: '#ebebeb',
            borderTopColor: '#ccc',
            borderRightColor: '#ccc',
            borderBottomColor: '#ccc',
            borderLeftColor: '#ccc',
        },
    }
});

export const SettingsDialog = () => {
    const styles = useStyles();
    const [open, setOpen] = React.useState(false);
    const [selectedStyle, setSelectedStyle] = React.useState('');
    const [converting, setConverting] = React.useState(false);
    const { citationStyles, selectedCitationStyle, setAuthData, logout } = useAuth();
    const { showToast } = useToast();

    React.useEffect(() => {
        setSelectedStyle(selectedCitationStyle);
    }, [selectedCitationStyle]);

    const handleCancel = () => {
        setOpen(false);
    }

    const handleDialogOpenChange = (e, data) => {
        setOpen(data.open);
    }

    const applySettings = async () => {
        const oldStyle = selectedCitationStyle;
        const newStyle = selectedStyle;
        
        // Update the style preference
        setAuthData({ selectedCitationStyle: selectedStyle });
        
        // If style changed and there are citations in the document, convert them
        if (oldStyle !== newStyle) {
            try {
                setConverting(true);
                
                // Check if there are citations in the document
                const mapping = await getStoredCitationMapping();
                
                if (mapping.citations && mapping.citations.length > 0) {
                    // Convert all citations to the new style
                    await refreshAllCitations(oldStyle, newStyle, showToast);
                } else {
                    // No citations to convert, just show success
                    showToast("Success", "Citation style updated successfully", { intent: "success" });
                }
            } catch (error) {
                showToast("Error", `Failed to convert citations: ${error.message}`, { intent: "error" });
            } finally {
                setConverting(false);
            }
        }
        
        setOpen(false);
    }

    return (
        <Dialog open={open} onOpenChange={handleDialogOpenChange} inertTrapFocus>
            <DialogTrigger disableButtonEnhancement>
                <Button
                    appearance="transparent"
                    className={styles.btn}
                    style={{ minWidth: '0px' }}
                    onClick={() => setOpen(true)}
                >
                    <img src="/assets/settings-32.png" alt="Settings icon" />
                </Button>
            </DialogTrigger>

            <DialogSurface className={styles.container}>
                <DialogBody style={{ height: '100%' }}>
                    <DialogTitle>
                        Settings
                        <Button
                            appearance="transparent"
                            className={styles.btnClose}
                            onClick={() => setOpen(false)}
                        >
                            <img src="/assets/close.png" alt="Close icon" />
                        </Button>
                    </DialogTitle>
                    <DialogContent className={styles.content}>
                        {/* Your citation form or selection UI */}
                        <p>Select active citation style for inserting to the document.</p>
                        <RadioGroup
                            value={selectedStyle}
                            onChange={(ev, data) => setSelectedStyle(data.value)}
                            layout="vertical" // ensures vertical alignment
                            className={styles.radioGroup}
                        >
                            {citationStyles.map((style) => (
                                <Radio
                                    key={style}
                                    value={style}
                                    label={style.toUpperCase()}
                                    checked={selectedStyle === style}
                                    onChange={(ev, data) => setSelectedStyle(data.value)}
                                />
                            ))}
                        </RadioGroup>
                        <div className={styles.divider}></div>
                        <Checkbox label="Persists style preference for future sessions">
                            
                        </Checkbox>
                    </DialogContent>
                    <DialogActions className={styles.buttons}>
                        <div style={{ display: 'grid', gap: '12px', width: '100%' }}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Button 
                                    appearance="primary" 
                                    onClick={applySettings}
                                    disabled={converting}
                                >
                                    {converting ? 'Converting...' : 'Apply'}
                                </Button>
                                <Button 
                                    appearance="outline" 
                                    onClick={handleCancel}
                                    disabled={converting}
                                >
                                    Cancel
                                </Button>
                            </div>
                            <Button 
                                appearance="subtle" 
                                onClick={() => {
                                    logout();
                                    setOpen(false);
                                    showToast("Signed Out", "You have been signed out successfully", { intent: "success" });
                                }}
                                className={styles.signOutButton}
                                style={{ width: "100%" }}
                            >
                                Sign Out
                            </Button>
                        </div>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
